import type { SeatingState } from '@peles/shared';

/**
 * Reading a plan's seating (`GET /events/:eventId/seating`) the way the floor
 * plan already reads guests. A guest's `tableNumber`/`seatNumber` mirror the
 * event's primary seating plan only, so the ceremony's plan would show the
 * dinner's tables; these put the plan on screen's seats in their place.
 */

export interface PlanSeat {
  tableNumber: number;
  seatNumber: number;
}

/** Each guest's lowest seat on the plan, at a numbered table. */
export function seatsOnPlan(state: SeatingState): Map<string, PlanSeat> {
  const seats = new Map<string, PlanSeat>();
  for (const table of state.tables) {
    if (table.tableNumber == null) continue;
    for (const seat of table.occupied) {
      const held = seats.get(seat.guestId);
      if (!held || seat.seatNumber < held.seatNumber) {
        seats.set(seat.guestId, { tableNumber: table.tableNumber, seatNumber: seat.seatNumber });
      }
    }
  }
  return seats;
}

/**
 * The guests as seated on the plan: each with its table and seat there, or
 * none. Until the plan's seating has loaded (or with no seating to read) the
 * guests are answered as they are.
 */
export function withPlanSeats<
  G extends { id: string; tableNumber?: number | null; seatNumber?: number | null },
>(guests: G[], state: SeatingState | undefined, planId: string | null | undefined): G[] {
  if (!state || !planId || state.plan.id !== planId) return guests;
  const seats = seatsOnPlan(state);
  return guests.map((guest) => {
    const seat = seats.get(guest.id);
    const tableNumber = seat?.tableNumber ?? null;
    const seatNumber = seat?.seatNumber ?? null;
    if (guest.tableNumber === tableNumber && guest.seatNumber === seatNumber) return guest;
    return { ...guest, tableNumber, seatNumber };
  });
}

/**
 * Who would lose a seat if these tables went: the parties seated at them and
 * how many chairs that is. Tables are named by their canvas uid.
 */
export function seatedAt(
  state: SeatingState | undefined,
  tableUids: Iterable<string>,
): { guestIds: string[]; seats: number } {
  if (!state) return { guestIds: [], seats: 0 };
  const uids = new Set(tableUids);
  const guestIds = new Set<string>();
  let seats = 0;
  for (const table of state.tables) {
    if (!uids.has(table.uid)) continue;
    for (const seat of table.occupied) {
      guestIds.add(seat.guestId);
      seats += 1;
    }
  }
  return { guestIds: [...guestIds], seats };
}
